import { Card, Divider, Flex, List, Pagination } from 'antd'
import { Dispatch, useState } from 'react'

import {
  useInstitutionServiceGetInstitutionActivitiesListEndpoint,
  useNotificationsServiceGetActivitiesListEndpoint,
  useUserServiceGetUserActivitiesListEndpoint
} from 'src/api/queries'
import { Permissions } from 'src/api/requests'
import {
  RoleSelect,
  PageBreadcrumbs,
  SearchBar,
  InternalInstitutionsSelect,
  RangePicker
} from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { ActivityCard, UsersInfinitySelect } from '../components'

import { useStyles } from './styles'

export const Activity = () => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const { userData, hasPermission } = useUser()
  const pagination = useTablePagination()

  const isAdmin = hasPermission(Permissions.NOTIFICATIONS_ACTIVITIES_VIEW_ALL)
  const isAdminInstitution = hasPermission(
    Permissions.NOTIFICATIONS_ACTIVITIES_VIEW_INSTITUTION
  )
  const isUser = hasPermission(
    Permissions.NOTIFICATIONS_ACTIVITIES_VIEW_PERSONAL
  )

  const hasUsersAccess = hasPermission([
    Permissions.IDENTITY_USERS_VIEW_ALL,
    Permissions.IDENTITY_USERS_VIEW_INSTITUTION
  ])

  const [search, setSearch] = useState('')

  const [user, setUser] = useState('')
  const [institution, setInstitution] = useState('')
  const [role, setRole] = useState('')

  const [dateTo, setDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    userId: isAdmin || isAdminInstitution ? user : undefined,
    legalEntityId:
      institution !== '' ? institution : userData?.currentLegalEntityId,
    roleId:
      isAdmin || isAdminInstitution
        ? role || undefined
        : userData?.currentRoleId,
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!dateFrom &&
      !!dateTo && {
        fromDate: dateFrom,
        toDate: dateTo
      })
  }

  const onFilterChange = (val: string, setState: Dispatch<string>) => {
    setState(val)
    resetPagination(pagination)
  }

  const { data, isLoading } = useNotificationsServiceGetActivitiesListEndpoint(
    {
      ...queryParams
    },
    [queryParams, isAdmin],
    {
      enabled: isAdmin
    }
  )

  const { data: instLogs, isLoading: isInstLogsLoading } =
    useInstitutionServiceGetInstitutionActivitiesListEndpoint(
      {
        ...queryParams
      },
      [queryParams, isAdminInstitution],
      {
        enabled: isAdminInstitution
      }
    )

  const { data: personalLogs, isLoading: isPersonalLoading } =
    useUserServiceGetUserActivitiesListEndpoint(
      { ...queryParams },
      [queryParams, isUser],
      { enabled: isUser }
    )

  const getDataSource = () => {
    if (isAdmin) return data?.data
    if (isAdminInstitution) return instLogs?.data
    if (isUser) return personalLogs?.data
  }

  const getTotalPagination = () => {
    if (isAdmin) return data?.totalCount
    if (isAdminInstitution) return instLogs?.totalCount
    if (isUser) return personalLogs?.totalCount
  }

  return (
    <PageBreadcrumbs
      title={t('activity.activity')}
      items={[{ title: t('activity.activity') }]}
    >
      <Flex vertical>
        <Flex justify="space-between" className="full-width" gap={8}>
          <SearchBar
            onSearch={(val) => onFilterChange(val, setSearch)}
            placeholder={t('activity.search_placeholder')}
          />

          <Flex gap={16} className="full-width">
            {(isAdminInstitution || isAdmin) && (
              <>
                {hasUsersAccess && (
                  <UsersInfinitySelect
                    className="full-width"
                    onClear={() => onFilterChange('', setUser)}
                    onSelect={(val) => onFilterChange(val, setUser)}
                  />
                )}

                {isAdmin && (
                  <>
                    <InternalInstitutionsSelect
                      className="full-width"
                      onClear={() => onFilterChange('', setInstitution)}
                      onSelect={(val) => onFilterChange(val, setInstitution)}
                    />
                    <RoleSelect
                      value={role}
                      className="full-width"
                      allowClear
                      onClear={() => onFilterChange('', setRole)}
                      onSelect={(val) => onFilterChange(val, setRole)}
                    />
                  </>
                )}
                {isAdminInstitution && (
                  <RoleSelect
                    legalEntityId={userData?.currentLegalEntityId || ''}
                    value={role}
                    className="full-width"
                    allowClear
                    onClear={() => onFilterChange('', setRole)}
                    onSelect={(val) => onFilterChange(val, setRole)}
                  />
                )}
              </>
            )}
            <RangePicker
              className="full-width"
              setDateTo={setDateTo}
              setDateFrom={setDateFrom}
              resetPagination={() => resetPagination(pagination)}
              placeholder={[
                t('notifications.from_date'),
                t('notifications.to_date')
              ]}
            />
          </Flex>
        </Flex>

        <Divider />

        <Card className={styles.card}>
          <List
            itemLayout="vertical"
            loading={isLoading || isInstLogsLoading || isPersonalLoading}
            dataSource={getDataSource()}
            renderItem={(item) => <ActivityCard {...item} key={item?.id} />}
          />
        </Card>

        <Pagination
          className={styles.pagination}
          {...pagination}
          total={getTotalPagination()}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
